import { ReactNode } from "react";
import { AitBorderRow } from "./AitBorderRow";
import { AitCell } from "./AitCell";
import { AitRowFirstCell } from "./AitRowFirstCell";
import { AitRowLastCell } from "./AitRowLastCell";
import { useTableContext } from "./TableContextProvider";
import { AitCellData } from "./functions/AitCellData";
import { getBaseId } from "./functions/getBaseId";
import {
  AitCellType,
  AitColumnRepeat,
  AitLocationBodyRow,
  AitTablePosition,
} from "./functions/interface";
import { trGetPositionFromLocation } from "./functions/trGetPositionFromLocation";

interface AitRowProps {
  location: AitLocationBodyRow;
}

/**
 * Render a table row with its cells.
 *
 * @template T - Row payload type
 * @param props - Component props
 * @param props.location - The row location within the table
 */
export const AitRow = <T,>({ location }: AitRowProps): ReactNode => {
  const { state } = useTableContext<T>();
  const id = getBaseId(location, state?.id);
  const thisRow = trGetPositionFromLocation(state, location, true).data;
  const columnRepeats = state?.processedTableData?.columnRepeats ?? [];
  const cells = thisRow?.cells ?? [];
  const spaceAfter = thisRow?.spaceAfter ?? false;
  const rowHeaderColumns = state?.processedTableData?.rowHeaderColumns ?? 0;

  return !thisRow ? null : (
    <>
      <tr id={`${id}`}>
        {/* Row group options */}
        <AitRowFirstCell location={location} />

        {/* All cells from row */}
        {cells.map((cell: AitCellData<T>, ci: number): React.ReactElement => {
          // Get cell from column repeat
          const cr: AitColumnRepeat | undefined =
            Array.isArray(columnRepeats) && columnRepeats.length > ci
              ? columnRepeats[ci]
              : // istanbul ignore next
                undefined;
          const isColumnRepeat =
            cr !== undefined && cr.colRepeat !== undefined
              ? cr.colRepeat.match(/^[[\]0,]+$/) === null
              : false;

          // Render object
          return (
            <AitCell
              key={
                isColumnRepeat && cr ? `${cell.aitid}-${JSON.stringify(cr.colRepeat)}` : cell.aitid
              }
              location={{
                ...location,
                position: AitTablePosition.bodyCell,
                column: ci,
                colRepeat: cr?.colRepeat ?? "",
                cellId: cell.aitid,
                cellType:
                  (cr?.columnIndex ??
                    // istanbul ignore next
                    1) < rowHeaderColumns
                    ? AitCellType.rowHeader
                    : AitCellType.body,
                isRepeat: location.isRepeat || isColumnRepeat,
                originalColumn:
                  columnRepeats[ci]?.columnIndex ??
                  // istanbul ignore next
                  ci,
              }}
            />
          );
        })}
        {/* End row cell */}
        <AitRowLastCell location={location} />
      </tr>
      {/* Additional row if required */}
      {(spaceAfter !== false || cells.some((c) => c.spaceAfterRepeat)) && (
        <AitBorderRow
          id={`${id}-spaceafter-row`}
          spaceAfter={true}
          noBorder={true}
        />
      )}
    </>
  );
};

AitRow.displayName = "AitRow";
